<script setup>
import {onMounted, ref, provide, computed} from "vue";
import {useCurrencyBuyAdsStore} from "@/stores/currencyBuyAds.js";
import {useUserStore} from "@/stores/user.js";
import {showMessage} from "@/utils/showMessage.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Icon} from "@iconify/vue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {Button} from "primevue";
import showAlert from "@/utils/showAlert.js";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import AddNewAd from "@/views/stocks/currencyBuyAds/Add.vue";
import SortIcon from "@/components/datatable/sortIcon.vue";
import SetPrice from "@/views/stocks/currencyPricing/Add.vue";

const adsStore = useCurrencyBuyAdsStore();
const userStore = useUserStore();
const loading = ref(false)
const deleting = ref(false)
const ads = ref(null)
const isShowAddNewAdModal = ref(false)
const hasSetAccess = computed(() => userStore.hasPermission("ORDERS_CURRENCY_UPDATE") || userStore.hasPermission('BIG_BOSS'))
const hasRemoveAccess = computed(() => userStore.hasPermission("ORDERS_CURRENCY_REMOVE") || userStore.hasPermission('BIG_BOSS'))
const selectedRecord = ref(null)

const fetchInitialData = async (refresh) => {
  try {
    if (!refresh) loading.value = true
    const res = await adsStore.action_getAllCurrencyBuyAds();
    if (res.result === true) {
      ads.value = res.data
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    loading.value = false
  }
}

/*const showAddNewAdModal = () => {
  isShowAddNewAdModal.value = true;
}*/

const removeAd = async (data) => {
  const ok = await showAlert(`You are deleting Ad (${data.description})`, 'Are you sure?', 'Yes', 'No')
  if (!ok) return

  try {
    deleting.value = true
    const res = await adsStore.action_removeAd({
      "game": data.game,
      "server": data.server
    })
    if (res.result === true) {
      showMessage('Ad deleted!', 'success')
      await fetchInitialData(true)
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    deleting.value = false
  }
}

function showAddNewAdModal({data}) {
  if (data) {
    selectedRecord.value = data
  } else {
    selectedRecord.value = null
  }
  isShowAddNewAdModal.value = true
}

onMounted(() => {
  fetchInitialData()
})

provide('isShowAddNewAdModal', isShowAddNewAdModal)
</script>

<template>
  <div class="flex flex-col gap-4 w-full h-full">
    <div class="flex flex-row justify-between gap-4 w-full">
      <Breadcrumbs/>
      <div v-if="hasSetAccess">
        <button class="btn btn-outline-dark py-1 text-xs font-medium rounded-md" @click="showAddNewAdModal">Add New
        </button>
      </div>
    </div>
    <div v-if="loading" class="flex items-center justify-center h-60">
      <Icon icon="eos-icons:bubble-loading" class="size-12"/>
    </div>
    <DataTable v-else
               :nullSortOrder="-1"
               :value="ads"
               scrollable
               scrollHeight="flex"
               :virtualScrollerOptions="{ itemSize: 30 }"
               resizableColumns
               removableSort
               stripedRows
               class="text-xs w-full"
               size="small"
               tableStyle="width: 100%; table-layout: fixed;"
               row-hover
               @rowClick="showAddNewAdModal"
               :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
    >
      <Column field="game" header="Game" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 10%; min-width: 70px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="server" header="Server" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 10%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="amount" header="Amount" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 8%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="toman" header="Toman" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 8%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="tomanFori" header="Toman Fori" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 8%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="tomanTetheri" header="Toman Tetheri" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 8%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="unit" header="Unit" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 7%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="minimum" header="Minimum" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 7%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="maximum" header="Maximum" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 7%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
      </Column>
      <Column field="description" header="Description" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 17%; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template></Column>
      <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
              class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
              style="width: 5%; min-width: 100px; height: 30px">
        <template #sorticon="{ sortOrder }">
          <SortIcon :sortOrder="sortOrder" />
        </template>
        <template #body="slotProps">
          <span class="block w-full max-w-max">
            {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
          </span>
        </template>
      </Column>
      <Column :exportable="false"
              style="width: 5%; min-width: 70px; height: 30px" v-if="hasRemoveAccess">
        <template #body="slotProps">
          <div class="flex flex-row gap-2 justify-end items-center w-full">
            <button @click.stop="removeAd(slotProps.data)">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="lucide lucide-trash2-icon lucide-trash-2">
                <path d="M10 11v6"/>
                <path d="M14 11v6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                <path d="M3 6h18"/>
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        </template>
      </Column>
    </DataTable>
    <AddNewAd v-if="isShowAddNewAdModal" @refresh="fetchInitialData" :selected-record="selectedRecord"/>
  </div>
</template>